import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import * as AuthActions from './auth.actions';
import { AuthService, ApiService } from '../../shared/services';

/**
 * Auth Effects
 * Handle side effects for auth-related actions
 */
@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ request }) =>
        this.authService.login(request).pipe(
          map(({ user, accessToken }) => AuthActions.loginSuccess({ user, accessToken })),
          catchError((error) =>
            of(
              AuthActions.loginFailure({
                error: error?.error?.message || 'Login failed',
              })
            )
          )
        )
      )
    )
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      switchMap(({ request }) =>
        this.authService.register(request).pipe(
          map(({ user, accessToken }) => AuthActions.registerSuccess({ user, accessToken })),
          catchError((error) =>
            of(
              AuthActions.registerFailure({
                error: error?.error?.message || 'Registration failed',
              })
            )
          )
        )
      )
    )
  );

  registerWithCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerWithCode),
      switchMap(({ request }) =>
        this.authService.registerWithCode(request).pipe(
          map(({ user, accessToken }) => AuthActions.registerWithCodeSuccess({ user, accessToken })),
          catchError((error) =>
            of(
              AuthActions.registerWithCodeFailure({
                error: error?.error?.message || 'Registration with code failed',
              })
            )
          )
        )
      )
    )
  );

  registerCreateTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerCreateTenant),
      switchMap(({ request }) => {
        return this.authService.registerCreateTenant(request).pipe(
          map(({ user, accessToken, tenantId, isTemporaryAdmin }) =>
            AuthActions.registerCreateTenantSuccess({ 
              user, 
              accessToken, 
              tenantId: tenantId || '', 
              isTemporaryAdmin: isTemporaryAdmin || false 
            })
          ),
          catchError((error) => {
            return of(
              AuthActions.registerCreateTenantFailure({
                error: error?.error?.message || 'Tenant creation failed',
              })
            );
          })
        );
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.authService.logout();
        this.ngZone.run(() => {
          void this.router.navigate(['/']);
        });
      })
    ),
    { dispatch: false }
  );

  authSuccessNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.loginSuccess,
          AuthActions.registerSuccess,
          AuthActions.registerWithCodeSuccess,
          AuthActions.registerCreateTenantSuccess
        ),
        tap(() => {
          this.ngZone.run(() => {
            void this.router.navigate(['/dashboard']);
          });
        })
      ),
    { dispatch: false }
  );

  getProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getProfile),
      switchMap(() =>
        this.apiService.getProfile().pipe(
          map((user) => AuthActions.getProfileSuccess({ user })),
          catchError((error) =>
            of(
              AuthActions.getProfileFailure({
                error: error?.error?.message || 'Failed to get profile',
              })
            )
          )
        )
      )
    )
  );

  enableMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.enableMfa),
      switchMap(() =>
        this.apiService.enableMfa().pipe(
          map(({ secret, qrCode, backupCodes }) =>
            AuthActions.enableMfaSuccess({ secret, qrCode, backupCodes })
          ),
          catchError((error) =>
            of(
              AuthActions.enableMfaFailure({
                error: error?.error?.message || 'Failed to enable MFA',
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private ngZone: NgZone
  ) {}
}
